import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { jsPDF } from 'jspdf';
import ImageCarousel from '../../Categories/components/ImageCarousel';
import HierarchicalTreeSelector from '../../Common/components/HierarchicalTreeSelector';

// Recursive component to render specification values
const SpecificationValue = ({ value }) => {
  // Handle arrays (like dimensions, voltage ranges, etc)
  if (Array.isArray(value)) {
    return (
      <div className="text-sm font-semibold text-gray-800">
        {value.join(' × ')}
      </div>
    );
  }
  
  // Handle nested objects (like capacity details, cutting thickness)
  if (typeof value === 'object' && value !== null) {
    return (
      <div className="space-y-1">
        {Object.entries(value).map(([subKey, subValue]) => (
          <div key={subKey} className="flex justify-between">
            <span className="text-sm text-gray-700">
              {subKey.replace(/_/g, ' ').split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}:
            </span>
            <span className="text-sm font-semibold text-gray-800 ml-2">
              {typeof subValue === 'object' && subValue !== null 
                ? JSON.stringify(subValue)
                : subValue?.toString()}
            </span>
          </div>
        ))}
      </div>
    );
  }
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {value ? 'Sim' : 'Não'}
      </span>
    );
  }
  
  // Handle numeric values
  if (typeof value === 'number') {
    return <span className="text-sm font-semibold text-gray-800">{value}</span>;
  }
  
  // Handle all other primitive values
  return <span className="text-sm text-gray-700">{value?.toString() || '-'}</span>;
};

const SubcategoryDetails = ({ subcategory, parent_type, parent, root_category, product_models, virtual_products = [], filter_values, breadcrumb_path = [], selected_model_id = null, categories = [] }) => {
  const [selectedModelId, setSelectedModelId] = useState(selected_model_id);
  const [selectedModel, setSelectedModel] = useState(selected_model_id ? product_models.find(m => m.id.toString() === selected_model_id.toString()) : null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    quantity: 1,
    phone: '',
    email: ''
  });
  const [quoteError, setQuoteError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templateLabelMap, setTemplateLabelMap] = useState({});
  const [templateGroups, setTemplateGroups] = useState(null);

  // Initialize filter state for products filtering
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  
  // Initialize tree selection with current subcategory
  const [selectedTreeItem, setSelectedTreeItem] = useState({
    id: subcategory.id,
    type: 'subcategory',
    item: subcategory,
    parentCategoryId: root_category?.id || parent?.id
  });
  
  // Fetch template labels for the category/subcategory
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        // Use the merged template endpoint to get both category and subcategory fields
        const response = await fetch(`/api/product_templates/merged?subcategory_id=${encodeURIComponent(subcategory.id)}`);
        if (response.ok) {
          const template = await response.json();
          
          // Build a simple key->label map from the template
          const labelMap = {};
          if (Array.isArray(template) && template.length > 0) {
            // Store the template structure for grouping
            setTemplateGroups(template);
            
            template.forEach(group => {
              if (group.fields && Array.isArray(group.fields)) {
                group.fields.forEach(field => {
                  if (field.key && field.label) {
                    labelMap[field.key] = field.label;
                  }
                });
              }
            });
          }
          setTemplateLabelMap(labelMap);
        }
      } catch (error) {
        console.error('Failed to fetch template:', error);
        // Silently fail - will fall back to auto-conversion
      }
    };
    
    fetchTemplate();
  }, [subcategory.id]);
  
  // Initialize expanded groups when model or template changes
  useEffect(() => {
    if (selectedModel && selectedModel.especificacoes) {
      const specs = groupSpecifications(selectedModel.especificacoes);
      const initialExpandedState = {};
      Object.keys(specs).forEach(group => {
        initialExpandedState[group] = true;
      });
      setExpandedGroups(initialExpandedState);
    }
  }, [selectedModel, templateGroups]); // Re-run when template loads

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event) => {
      const pathParts = window.location.pathname.split('/');
      const modelIndex = pathParts.indexOf('model');
      
      if (modelIndex !== -1 && modelIndex + 1 < pathParts.length) {
        const modelId = pathParts[modelIndex + 1];
        const model = product_models.find(m => m.id.toString() === modelId);
        
        setSelectedModelId(modelId);
        setSelectedModel(model);
        
        if (model) {
          const specs = groupSpecifications(model.especificacoes);
          const initialExpandedState = {};
          Object.keys(specs).forEach(group => {
            initialExpandedState[group] = true;
          });
          setExpandedGroups(initialExpandedState);
        }
      } else {
        setSelectedModelId('');
        setSelectedModel(null);
        setExpandedGroups({});
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [product_models]); // Include product_models in dependencies since we use it in the handler
  
  // Local helper function instead of importing
  const getImageUrl = (imagePath) => {
    console.log('Image path received:', imagePath);
    if (!imagePath) {
      console.log('No image path, using placeholder');
      return '/assets/placeholder.jpg';
    }
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('/rails/active_storage/')) {
      console.log('Using full URL:', imagePath);
      return imagePath;
    }
    if (imagePath.startsWith('/assets/')) {
      console.log('Using assets path:', imagePath);
      return imagePath;
    }
    console.log('Adding assets prefix to:', imagePath);
    return `/assets/${imagePath}`;
  };
  
  // Determine back link based on parent type
  const backLink = parent_type === 'category' 
    ? `/?category=${parent.id}` 
    : `/?subcategory=${parent.id}`;
  
  // Filter models based on selected filter value
  // Use filtered products from the new filter system
  // Convert virtual products back to product models for compatibility
  const filteredModels = filteredProducts.length > 0 
    ? product_models.filter(model => 
        filteredProducts.some(vp => vp.id === model.id)
      )
    : product_models;
    
  // Get available models for the dropdown
  const availableModels = filteredModels.map(model => ({
    id: model.id,
    name: model.name
  }));
  
  // Reset model selection when filters change
  useEffect(() => {
    if (hasActiveFilters) {
      setSelectedModelId('');
      setSelectedModel(null);
      setQuoteData(null);
      setShowQuoteForm(false);
      setQuoteForm({
        quantity: 1,
        phone: '',
        email: ''
      });
    }
  }, [hasActiveFilters]);
  
  const handleModelChange = (e) => {
    const modelId = e.target.value;
    setSelectedModelId(modelId);
    
    // Reset quote states when changing models
    setQuoteData(null);
    setShowQuoteForm(false);
    setQuoteForm({
      quantity: 1,
      phone: '',
      email: ''
    });
    
    if (modelId) {
      const model = product_models.find(m => m.id.toString() === modelId);
      setSelectedModel(model);
      
      // Update URL without reloading
      const newUrl = `/subcategories/${subcategory.id}/model/${modelId}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
      
      // Initialize all groups as expanded when a model is selected
      const specs = groupSpecifications(model.especificacoes);
      const initialExpandedState = {};
      Object.keys(specs).forEach(group => {
        initialExpandedState[group] = true;
      });
      setExpandedGroups(initialExpandedState);
    } else {
      setSelectedModel(null);
      setExpandedGroups({});
      
      // Remove model from URL when no model is selected
      const newUrl = `/subcategories/${subcategory.id}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    }
  };
  
  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };
  
  
  // Helper function to check if a value should be displayed
  const isValidValue = (value) => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
    return true;
  };

  // Group specifications into categories for better organization
  const groupSpecifications = (specs) => {
    // If we have template groups, use them
    if (templateGroups && templateGroups.length > 0) {
      const groups = {};
      
      // Create groups based on template structure
      templateGroups.forEach(templateGroup => {
        const groupName = templateGroup.title || 'Outros';
        groups[groupName] = {};
        
        // Add specifications that match template fields
        if (templateGroup.fields && Array.isArray(templateGroup.fields)) {
          templateGroup.fields.forEach(field => {
            if (field.key && specs[field.key] !== undefined && isValidValue(specs[field.key])) {
              groups[groupName][field.key] = specs[field.key];
            }
          });
        }
      });
      
      // Add ungrouped specifications to "Outros"
      const templateKeys = new Set();
      templateGroups.forEach(group => {
        if (group.fields) {
          group.fields.forEach(field => {
            if (field.key) templateKeys.add(field.key);
          });
        }
      });
      
      const ungroupedSpecs = {};
      Object.entries(specs).forEach(([key, value]) => {
        if (!templateKeys.has(key) && key !== 'price_usd' && isValidValue(value)) {
          ungroupedSpecs[key] = value;
        }
      });
      
      if (Object.keys(ungroupedSpecs).length > 0) {
        groups['Outros'] = ungroupedSpecs;
      }
      
      // Remove empty groups
      return Object.fromEntries(
        Object.entries(groups).filter(([_, value]) => Object.keys(value).length > 0)
      );
    }
    
    // Fallback to original hardcoded grouping
    const groups = {
      'Dimensões e Capacidade': {},
      'Especificações Técnicas': {},
      'Sistema e Controle': {},
      'Energia e Potência': {},
      'Outros': {}
    };
    
    Object.entries(specs).forEach(([key, value]) => {
      // Skip price and invalid values
      if (key === 'price_usd' || !isValidValue(value)) return;
      
      // Dimensions and capacity related
      if (key.includes('area') || key.includes('dimensoes') || key.includes('espessura') || 
          key.includes('capacidade') || key.includes('peso')) {
        groups['Dimensões e Capacidade'][key] = value;
      }
      // Technical specifications
      else if (key.includes('velocidade') || key.includes('precisao') || key.includes('aceleracao') ||
              key.includes('comprimento') || key.includes('diametro') || key.includes('faixa')) {
        groups['Especificações Técnicas'][key] = value;
      }
      // System and control
      else if (key.includes('sistema') || key.includes('controle') || key.includes('cabecote') ||
              key.includes('ressonador') || key.includes('modo')) {
        groups['Sistema e Controle'][key] = value;
      }
      // Power related
      else if (key.includes('potencia') || key.includes('tensao') || key.includes('motor') ||
              key.includes('chiller') || key.includes('estabilizador') || key.includes('compressor')) {
        groups['Energia e Potência'][key] = value;
      }
      // Other specs
      else {
        groups['Outros'][key] = value;
      }
    });
    
    // Remove empty groups
    return Object.fromEntries(
      Object.entries(groups).filter(([_, value]) => Object.keys(value).length > 0)
    );
  };
  
  // Get group icon based on group name
  const getGroupIcon = (groupName) => {
    switch (groupName) {
      case 'Dimensões e Capacidade':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
          </svg>
        );
      case 'Especificações Técnicas':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path>
          </svg>
        );
      case 'Sistema e Controle':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
        );
      case 'Energia e Potência':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        );
    }
  };
  
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteError(null);
    setIsSubmitting(true);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      
      const response = await fetch(`/api/product_models/${selectedModel.id}/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          quote: quoteForm
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.errors ? Object.values(data.errors).join(', ') : 'Failed to generate quote');
      }

      const data = await response.json();
      setQuoteData(data);
      setShowQuoteForm(false);
    } catch (error) {
      console.error('Quote error:', error);
      setQuoteError(error.message || 'Failed to generate quote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value, currency = 'USD') => {
    return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'pt-BR', {
      style: 'currency',
      currency: currency
    }).format(value);
  };
  
  // Add generatePDF function
  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Add logo and header
    doc.setFontSize(20);
    doc.setTextColor(232, 88, 14); 
    doc.text('Importe Sua Máquina', 105, 20, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('ORÇAMENTO', 105, 30, { align: 'center' });
    
    // Add divider line
    doc.setDrawColor(232, 88, 14);
    doc.setLineWidth(0.5);
    doc.line(20, 35, 190, 35);
    
    // Add subcategory first image
    const imageUrl = subcategory.image_urls?.[0] || (subcategory.image_url ? getImageUrl(subcategory.image_url) : null);
    
    if (imageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        // Add image with border/box
        const maxWidth = 100;
        const maxHeight = 70;
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        const width = img.width * ratio;
        const height = img.height * ratio;
        const xPos = (210 - width) / 2; // Center horizontally on A4 page
        const yPos = 45;
        
        // Draw a box around the image
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.rect(xPos - 5, yPos - 5, width + 10, height + 10);
        
        // Add shadow effect with a subtle gray background
        doc.setFillColor(245, 245, 245);
        doc.rect(xPos - 3, yPos - 3, width + 6, height + 6, 'F');
        
        // Add the image
        doc.addImage(img, 'JPEG', xPos, yPos, width, height);
        
        // Add model name centered under the image
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(`Modelo: ${selectedModel.name}`, 105, yPos + height + 12, { align: 'center' });
        
        // Continue with rest of PDF below image
        addPDFContent(yPos + height + 25);
      };
      img.onerror = function() {
        // Continue without image
        addPDFContent(45);
      };
      img.src = imageUrl;
    } else {
      // No image, continue
      addPDFContent(45);
    }
    
    function addPDFContent(startY) {
      // Add model details
      doc.setFontSize(12);
      doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, 20, startY);
      doc.text(`Taxa de câmbio: ${formatCurrency(1, 'USD')} = ${formatCurrency(quoteData.exchange_rate, 'BRL')}`, 20, startY + 7);
    
      // Add quote details table
      doc.setFontSize(11);
      doc.text('Descrição', 20, startY + 23);
      doc.text('Valor (BRL)', 150, startY + 23);
      doc.text('Valor (USD)', 180, startY + 23);
      
      doc.setDrawColor(200, 200, 200);
      doc.line(20, startY + 26, 190, startY + 26);
      
      // Row 1: Base price
      doc.text('Valor base da máquina', 20, startY + 33);
      doc.text(formatCurrency(quoteData.totals_brl.base_price_with_margin, 'BRL').replace('R$', ''), 150, startY + 33);
      doc.text(formatCurrency(quoteData.totals.base_price_with_margin, 'USD').replace('$', ''), 180, startY + 33);
      
      // Row 2: Shipping
      doc.text(`Frete Marítimo e Seguros${quoteData.totals.shipping_type === 'percentage' ? ` (${quoteData.totals.shipping_percent}%)` : ''}`, 20, startY + 40);
      doc.text(formatCurrency(quoteData.totals_brl.shipping, 'BRL').replace('R$', ''), 150, startY + 40);
      doc.text(formatCurrency(quoteData.totals.shipping, 'USD').replace('$', ''), 180, startY + 40);

      // Row 3: Taxes
      doc.text(`Impostos${quoteData.totals.taxes_type === 'percentage' ? ` (${quoteData.totals.taxes_percent}%)` : ''}`, 20, startY + 47);
      doc.text(formatCurrency(quoteData.totals_brl.taxes, 'BRL').replace('R$', ''), 150, startY + 47);
      doc.text(formatCurrency(quoteData.totals.taxes, 'USD').replace('$', ''), 180, startY + 47);

      // Row 4: Other expenses
      doc.text(`Demais Despesas${quoteData.totals.other_type === 'percentage' ? ` (${quoteData.totals.other_percent}%)` : ''}`, 20, startY + 54);
      doc.text(formatCurrency(quoteData.totals_brl.other, 'BRL').replace('R$', ''), 150, startY + 54);
      doc.text(formatCurrency(quoteData.totals.other, 'USD').replace('$', ''), 180, startY + 54);
      
      // Total line
      doc.line(20, startY + 58, 190, startY + 58);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');

      // Show range if error margin exists, otherwise show single total
      if (quoteData.totals.error_margin_percent > 0) {
        // With error margin - show range in two lines (BRL and USD)
        doc.text('TOTAL (BRL)', 20, startY + 65);
        const lowerBRL = formatCurrency(quoteData.totals_brl.total_lower_bound, 'BRL');
        const upperBRL = formatCurrency(quoteData.totals_brl.total_upper_bound, 'BRL');
        doc.text(`${lowerBRL} ~ ${upperBRL}`, 190, startY + 65, { align: 'right' });

        doc.text('TOTAL (USD)', 20, startY + 72);
        const lowerUSD = formatCurrency(quoteData.totals.total_lower_bound, 'USD');
        const upperUSD = formatCurrency(quoteData.totals.total_upper_bound, 'USD');
        doc.text(`${lowerUSD} ~ ${upperUSD}`, 190, startY + 72, { align: 'right' });
      } else {
        // No error margin - show single value in original format
        doc.text('TOTAL', 20, startY + 65);
        doc.text(formatCurrency(quoteData.totals_brl.total, 'BRL').replace('R$', ''), 150, startY + 65);
        doc.text(formatCurrency(quoteData.totals.total, 'USD').replace('$', ''), 180, startY + 65);
      }
      
      // Footer with contact info - adjust Y position based on whether we have range
      const footerY = quoteData.totals.error_margin_percent > 0 ? startY + 90 : startY + 83;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.text('Para mais informações, entre em contato:', 105, footerY, { align: 'center' });
      doc.text('Telefone: (51) 98173-8538', 105, footerY + 7, { align: 'center' });

      // Add divider line
      doc.setDrawColor(232, 88, 14);
      doc.line(20, footerY + 20, 190, footerY + 20);
      
      const text = `Observação: O presente orçamento está sujeito às variações cambiais, de frete internacional e às demais taxas, tarifas e encargos incidentes no processo de importação e nacionalização da mercadoria, podendo os valores pactuados sofrer ajustes de acordo com as oscilações verificadas até a efetiva conclusão do processo.`
      // Add terms with text wrapping
      doc.setFontSize(8);
      const splitText = doc.splitTextToSize(text, 170); // 170mm width for wrapping
      doc.text(splitText, 105, footerY + 30, { align: 'center' });
    
      // Save the PDF
      doc.save(`orcamento_${selectedModel.name.replace(/\s+/g, '_')}.pdf`);
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        <div className="mb-6 flex items-center text-sm">
          <a href="/" className="text-orange-500 hover:text-orange-600">Home</a>
          <span className="mx-2 text-gray-500">/</span>
          
          {/* Show category first */}
          <a href={`/?category=${root_category?.id || subcategory.category?.id || parent.id}`} className="text-orange-500 hover:text-orange-600">
            {root_category?.name || subcategory.category?.name || (parent_type === 'category' ? parent.name : 'Categorias')}
          </a>
          <span className="mx-2 text-gray-500">/</span>
          
          {/* Show breadcrumb path if exists */}
          {breadcrumb_path.length > 0 && (
            <>
              {breadcrumb_path.map((item, index) => (
                <React.Fragment key={item.id}>
                  <a 
                    href={`/?subcategory=${item.id}`} 
                    className="text-orange-500 hover:text-orange-600"
                  >
                    {item.name}
                  </a>
                  <span className="mx-2 text-gray-500">/</span>
                </React.Fragment>
              ))}
            </>
          )}
          
          {/* Show immediate parent if no breadcrumb path and parent is subcategory */}
          {breadcrumb_path.length === 0 && parent_type === 'subcategory' && (
            <>
              <a href={`/?subcategory=${parent.id}`} className="text-orange-500 hover:text-orange-600">
                {parent.name}
              </a>
              <span className="mx-2 text-gray-500">/</span>
            </>
          )}
          
          <span className="text-gray-700">{subcategory.name}</span>
        </div>
        
        <div className="flex md:flex-row flex-col gap-8">
          {/* Left Sidebar - Tree Navigation */}
          <div className="md:w-1/4 w-full">
            <HierarchicalTreeSelector
              categories={categories}
              selectedItem={selectedTreeItem}
              onSelectionChange={(selection) => {
                // Handle tree selection - always navigate to main page
                if (selection) {
                  if (selection.type === 'category') {
                    // Go to main page with category selected
                    window.location.href = `/?category=${selection.id}`;
                  } else if (selection.type === 'subcategory') {
                    // Check if it's a leaf subcategory
                    if (selection.item.is_leaf) {
                      // Only stay on subcategory page if it's the current one
                      if (selection.id === subcategory.id) {
                        return; // Stay on current page
                      }
                      window.location.href = `/subcategories/${selection.id}`;
                    } else {
                      // Go to main page with subcategory selected
                      window.location.href = `/?subcategory=${selection.id}`;
                    }
                  }
                } else {
                  window.location.href = '/';
                }
              }}
              products={[]} // No product filtering in this view
              onFilterChange={() => {}} // No filtering needed
            />
          </div>
          
          {/* Main Content */}
          <div className="md:w-3/4 w-full">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Subcategory Header */}
              <div className="p-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{subcategory.name}</h1>
                
                {/* Image Display - Show model images if selected and available, otherwise subcategory images */}
                <div className="mb-6">
                  {(() => {
                    // Determine which images to show
                    const hasModelImages = selectedModel?.image_urls && selectedModel.image_urls.length > 0;
                    const hasSubcategoryImages = subcategory.image_urls && subcategory.image_urls.length > 0;
                    const hasSubcategoryImage = subcategory.image_url;
                    
                    if (hasModelImages) {
                      // Show model images if available
                      return (
                        <ImageCarousel 
                          images={selectedModel.image_urls}
                          alt={selectedModel.name}
                          showThumbnails={true}
                          allowFullscreen={true}
                        />
                      );
                    } else if (hasSubcategoryImages) {
                      // Fall back to subcategory images
                      return (
                        <ImageCarousel 
                          images={subcategory.image_urls}
                          alt={subcategory.name}
                          showThumbnails={true}
                          allowFullscreen={true}
                        />
                      );
                    } else if (hasSubcategoryImage) {
                      // Fall back to single subcategory image
                      return (
                        <div className="h-80 bg-gray-200 rounded-lg">
                          <img 
                            src={getImageUrl(subcategory.image_url)} 
                            alt={subcategory.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      );
                    } else {
                      // No images available
                      return (
                        <div className="h-80 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-500">Sem imagem disponível</span>
                        </div>
                      );
                    }
                  })()}
                </div>
                
                <div className="prose max-w-none">
                  <p>{subcategory.description}</p>
                </div>
              </div>
              
              {/* Model Selection Section */}
              <div className="border-t border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Modelos Disponíveis</h2>
                
                {availableModels.length > 0 ? (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Modelo
                    </label>
                    <select 
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      value={selectedModelId}
                      onChange={handleModelChange}
                    >
                      <option value="">Selecione um modelo</option>
                      {availableModels.map(model => (
                        <option key={model.id} value={model.id}>{model.name}</option>
                      ))}
                    </select>
                    {availableModels.length > 10 && (
                      <p className="text-sm text-gray-500 mt-1">
                        Role a lista acima para ver todos os modelos disponíveis ({availableModels.length} itens)
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">Não há modelos disponíveis com os filtros selecionados.</p>
                )}
              </div>
              
              {/* Selected Model Details */}
              {selectedModel && (
                <div className="border-t border-gray-200 p-6">
                  <div className="flex items-center mb-6">
                    <h2 className="ml-4 text-2xl font-bold text-gray-900">Modelo {selectedModel.name}</h2>
                  </div>
                  
                  {/* Specifications Accordion */}
                  <div className="space-y-4 mb-8">
                    {Object.entries(groupSpecifications(selectedModel.especificacoes)).map(([groupName, specs]) => (
                      <div key={groupName} className="border border-gray-200 rounded-lg overflow-hidden">
                        <button 
                          className={`w-full flex items-center justify-between px-4 py-3 text-left ${
                            expandedGroups[groupName] ? 'bg-orange-50' : 'bg-gray-50'
                          }`}
                          onClick={() => toggleGroup(groupName)}
                        >
                          <div className="flex items-center">
                            <span className={`flex-shrink-0 mr-2 ${expandedGroups[groupName] ? 'text-orange-500' : 'text-gray-500'}`}>
                              {getGroupIcon(groupName)}
                            </span>
                            <h3 className={`text-md font-semibold ${expandedGroups[groupName] ? 'text-orange-700' : 'text-gray-900'}`}>
                              {groupName}
                            </h3>
                          </div>
                          <svg 
                            className={`w-5 h-5 transition-transform ${expandedGroups[groupName] ? 'transform rotate-180 text-orange-500' : 'text-gray-500'}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {expandedGroups[groupName] && (
                          <div className="divide-y divide-gray-200">
                            {Object.entries(specs).map(([key, value]) => {
                              const displayKey = key === 'price_usd' 
                                ? 'Preço (USD)' 
                                : templateLabelMap[key] || key.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                              
                              const isNestedObject = typeof value === 'object' && value !== null && !Array.isArray(value);
                              
                              return (
                                <div key={key} className="p-4">
                                  {isNestedObject ? (
                                    <>
                                      <div className="text-sm font-medium text-gray-900 mb-2">
                                        {displayKey}
                                      </div>
                                      <SpecificationValue value={value} />
                                    </>
                                  ) : (
                                    <div className="flex justify-between items-center">
                                      <div className="text-sm font-medium text-gray-900">
                                        {displayKey}
                                      </div>
                                      <div>
                                        {key === 'price_usd' 
                                          ? <span className="text-sm font-semibold text-gray-800">
                                              ${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                          : <SpecificationValue value={value} />}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Quote Section */}
                  {!quoteData && (
                    <div className="mt-6">
                      {!showQuoteForm ? (
                        <button
                          onClick={() => setShowQuoteForm(true)}
                          className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md transition-colors"
                        >
                          Solicitar Orçamento
                        </button>
                      ) : (
                        <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Preencha os dados para gerar o orçamento</h3>
                          <form onSubmit={handleQuoteSubmit} className="space-y-4">
                            <div>
                              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                                Quantidade
                              </label>
                              <input
                                type="number"
                                id="quantity"
                                min="1"
                                value={quoteForm.quantity}
                                onChange={(e) => setQuoteForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                              />
                            </div>
                            <div>
                              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                Telefone *
                              </label>
                              <input
                                type="tel"
                                id="phone"
                                required
                                value={quoteForm.phone}
                                onChange={(e) => setQuoteForm(prev => ({ ...prev, phone: e.target.value }))}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                              />
                            </div>
                            <div>
                              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                E-mail *
                              </label>
                              <input
                                type="email"
                                id="email"
                                required
                                value={quoteForm.email}
                                onChange={(e) => setQuoteForm(prev => ({ ...prev, email: e.target.value }))}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                              />
                            </div>
                            {quoteError && (
                              <div className="text-red-600 text-sm">{quoteError}</div>
                            )}
                            <div className="flex justify-end space-x-3">
                              <button
                                type="button"
                                onClick={() => setShowQuoteForm(false)}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md transition-colors"
                              >
                                Cancelar
                              </button>
                              <button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md transition-colors disabled:opacity-50"
                              >
                                {isSubmitting ? 'Gerando...' : 'Gerar Orçamento'}
                              </button>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Quote Results */}
                  {quoteData && (
                    <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Orçamento Gerado</h3>
                      <div className="text-sm text-gray-500 mb-4">
                        Taxa de câmbio atual: {formatCurrency(1, 'USD')} = {formatCurrency(quoteData.exchange_rate, 'BRL')}
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-gray-600">Valor base da máquina:</span>
                          <div className="text-right">
                            <div className="font-medium">{formatCurrency(quoteData.totals_brl.base_price_with_margin, 'BRL')}</div>
                            <div className="text-sm text-gray-500">({formatCurrency(quoteData.totals.base_price_with_margin, 'USD')})</div>
                          </div>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-gray-600 flex items-center gap-1">
                            Frete Marítimo e Seguros{quoteData.totals.shipping_type === 'percentage' ? ` (${quoteData.totals.shipping_percent}%)` : ''}:
                            <span className="relative group">
                              <svg className="w-4 h-4 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-80 p-4 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10">
                                <p className="font-semibold mb-2">Frete Marítimo e Seguros</p>
                                <p>O valor fixo do orçamento é uma estimativa inicial. O custo real pode mudar devido à Volatilidade Global (demanda de contêineres, custo de combustível e geopolítica), Alteração de Tarifas das transportadoras e seguradoras e mudanças no Seguro de Carga baseadas no valor da mercadoria ou risco da rota.</p>
                              </div>
                            </span>
                          </span>
                          <div className="text-right">
                            <div className="font-medium">{formatCurrency(quoteData.totals_brl.shipping, 'BRL')}</div>
                            <div className="text-sm text-gray-500">({formatCurrency(quoteData.totals.shipping, 'USD')})</div>
                          </div>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-gray-600 flex items-center gap-1">
                            Impostos{quoteData.totals.taxes_type === 'percentage' ? ` (${quoteData.totals.taxes_percent}%)` : ''}:
                            <span className="relative group">
                              <svg className="w-4 h-4 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-80 p-4 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10">
                                <p className="font-semibold mb-2">Impostos</p>
                                <p className="mb-2">A alíquota de impostos está sujeita a ajustes por dois motivos principais:</p>
                                <p className="mb-2"><strong>Variação Cambial:</strong> Os impostos são calculados sobre o Valor Aduaneiro em Reais, que flutua diretamente com a taxa de câmbio aumentando a base de cálculo se o dólar subir.</p>
                                <p><strong>Mudanças na Legislação:</strong> Alterações nas alíquotas (percentuais) de Imposto de Importação, IPI ou ICMS pelo governo federal ou estadual podem impactar o total.</p>
                              </div>
                            </span>
                          </span>
                          <div className="text-right">
                            <div className="font-medium">{formatCurrency(quoteData.totals_brl.taxes, 'BRL')}</div>
                            <div className="text-sm text-gray-500">({formatCurrency(quoteData.totals.taxes, 'USD')})</div>
                          </div>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-gray-600 flex items-center gap-1">
                            Demais Despesas{quoteData.totals.other_type === 'percentage' ? ` (${quoteData.totals.other_percent}%)` : ''}:
                            <span className="relative group">
                              <svg className="w-4 h-4 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-80 p-4 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10">
                                <p className="font-semibold mb-2">Demais Despesas</p>
                                <p className="mb-2">Essas despesas, que cobrem os serviços operacionais no Brasil, podem mudar devido a:</p>
                                <p className="mb-2"><strong>Custos Portuários/Aeroportuários:</strong> Reajustes periódicos nas tarifas de armazenagem, manuseio (THC) e outras taxas dos Recintos Alfandegados.</p>
                                <p className="mb-2"><strong>Serviços de Desembaraço:</strong> Reajustes nas taxas de despachantes e agentes logísticos.</p>
                                <p><strong>Taxas Oficiais:</strong> Alterações em taxas como a de Utilização do SISCOMEX.</p>
                              </div>
                            </span>
                          </span>
                          <div className="text-right">
                            <div className="font-medium">{formatCurrency(quoteData.totals_brl.other, 'BRL')}</div>
                            <div className="text-sm text-gray-500">({formatCurrency(quoteData.totals.other, 'USD')})</div>
                          </div>
                        </div>
                        <div className="flex justify-between py-3 border-b border-gray-400">
                          <span className="text-gray-800 font-semibold flex items-center gap-1">
                            Total:
                            <span className="relative group">
                              <svg className="w-4 h-4 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-80 p-4 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10">
                                <p className="font-semibold mb-2">Justificativa sobre a variação do valor final da importação:</p>
                                <p className="mb-2">O valor final de uma importação pode variar devido a diversos fatores além do preço da máquina. Custos fixos como transporte, carga, descarga e armazenagem são cobrados independentemente do valor do produto, o que faz com que pesem mais em importações de menor valor e se diluam em máquinas mais caras.</p>
                                <p className="mb-2">Além disso, o canal de fiscalização definido pela Receita Federal (verde, amarelo ou vermelho) influencia o tempo de liberação e pode aumentar a armazenagem. Também há variação conforme o tipo de transporte — se a carga vem sozinha ou compartilhada — e possíveis imprevistos na conferência, como erros de interpretação por parte dos fiscais.</p>
                                <p>Por esses motivos, não é possível determinar um valor exato e fixo de importação, pois cada processo pode apresentar custos diferentes conforme suas circunstâncias.</p>
                              </div>
                            </span>
                          </span>
                          <div className="text-right">
                            {quoteData.totals.error_margin_percent > 0 ? (
                              <>
                                <div className="text-xl font-bold text-orange-600">
                                  {formatCurrency(quoteData.totals_brl.total_lower_bound, 'BRL')} ~ {formatCurrency(quoteData.totals_brl.total_upper_bound, 'BRL')}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ({formatCurrency(quoteData.totals.total_lower_bound, 'USD')} ~ {formatCurrency(quoteData.totals.total_upper_bound, 'USD')})
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-xl font-bold text-orange-600">{formatCurrency(quoteData.totals_brl.total, 'BRL')}</div>
                                <div className="text-sm text-gray-500">({formatCurrency(quoteData.totals.total, 'USD')})</div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          onClick={generatePDF}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-md transition-colors"
                        >
                          Baixar PDF
                        </button>
                        <a
                          href={`https://wa.me/5551981738538?text=${encodeURIComponent(
                            `*Orçamento Importe Sua Máquina*\n\n` +
                            `*Modelo:* ${selectedModel.name}\n\n` +
                            `*Taxa de câmbio:* ${formatCurrency(1, 'USD')} = ${formatCurrency(quoteData.exchange_rate, 'BRL')}\n\n` +
                            `*Detalhes:*\n` +
                            `- Valor base: ${formatCurrency(quoteData.totals_brl.base_price_with_margin, 'BRL')} (${formatCurrency(quoteData.totals.base_price_with_margin, 'USD')})\n` +
                            `- Frete e Seguros${quoteData.totals.shipping_type === 'percentage' ? ` (${quoteData.totals.shipping_percent}%)` : ''}: ${formatCurrency(quoteData.totals_brl.shipping, 'BRL')} (${formatCurrency(quoteData.totals.shipping, 'USD')})\n` +
                            `- Impostos${quoteData.totals.taxes_type === 'percentage' ? ` (${quoteData.totals.taxes_percent}%)` : ''}: ${formatCurrency(quoteData.totals_brl.taxes, 'BRL')} (${formatCurrency(quoteData.totals.taxes, 'USD')})\n` +
                            `- Demais Despesas${quoteData.totals.other_type === 'percentage' ? ` (${quoteData.totals.other_percent}%)` : ''}: ${formatCurrency(quoteData.totals_brl.other, 'BRL')} (${formatCurrency(quoteData.totals.other, 'USD')})\n\n` +
                            (quoteData.totals.error_margin_percent > 0
                              ? `*TOTAL: ${formatCurrency(quoteData.totals_brl.total_lower_bound, 'BRL')} ~ ${formatCurrency(quoteData.totals_brl.total_upper_bound, 'BRL')}* (${formatCurrency(quoteData.totals.total_lower_bound, 'USD')} ~ ${formatCurrency(quoteData.totals.total_upper_bound, 'USD')})\n\n`
                              : `*TOTAL: ${formatCurrency(quoteData.totals_brl.total, 'BRL')}* (${formatCurrency(quoteData.totals.total, 'USD')})\n\n`) +
                            `Olá, gostaria de fazer uma proposta para o modelo acima conforme orçamento detalhado.`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition-colors"
                        >
                          Fazer proposta via WhatsApp
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

SubcategoryDetails.propTypes = {
  subcategory: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    filter_type: PropTypes.string
  }).isRequired,
  parent_type: PropTypes.oneOf(['category', 'subcategory']).isRequired,
  parent: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  root_category: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }),
  product_models: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  virtual_products: PropTypes.array,
  filter_values: PropTypes.arrayOf(PropTypes.string),
  breadcrumb_path: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  selected_model_id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ])
};

SpecificationValue.propTypes = {
  value: PropTypes.any
};

export default SubcategoryDetails; 